/**
 * /dashboard/legislation (§2.7, §2.7.1; screenshots/25, 31–34).
 * Tabs: Up for a vote · Following · Roll calls · Filed · Forecast accuracy. Every tab paints from the
 * snapshot the Worker inlines (snapshot:legislation); j/k move between rows, ↵ opens one.
 */
import { createContext, useContext, useMemo, useRef, useState } from "react";
import { ArrowDownUp, CalendarDays, Check, ChevronsUpDown, Download, FileText, Gauge, Phone, Plus, Shapes, Tag, Users } from "lucide-react";
import { Face } from "../components/shared/Face";
import { List, ListRow, RowCell, RowMain, RowSub, RowTitle } from "../components/shared/List";
import { NoWard } from "../components/shared/States";
import { ConnectionStatus } from "../components/shared/ConnectionStatus";
import { TypeBadge } from "../components/following/TypeBadge";
import { findFollow, followsStore, useFollows } from "../stores/followsStore";
import { navigate } from "../app/router";
import { can, type OrgRole, type UserGroup } from "../nav/navConfig";
import { ForecastChip, ProjectionBar, TensionGauge } from "../forecast/ForecastViews";
import { useListKeyboard } from "../hooks/useTabs";
import { LegislationRow, backtestSignal, forecastSignal, href, type Signal } from "./LegislationRow";
import { accuracySummary, councilLabel, groupFiled, wardSaid } from "./logic";
import { LIST_TABS, listStore, useList, type ListTab } from "./listStore";
import { CountBadge, GroupLabel, Kicker, LinkArrow, SectionHeader, SelectButton, ViewSwitch, TabBar } from "./ui";
import type { FiledItem, FollowingTab, LegislationSummary, ListData } from "./types";

const openAddFollow = (type?: string) => dispatchEvent(new CustomEvent("palette:open", { detail: { mode: "follow", type } }));

export function LegislationListPage({ group, role, tab, onTab }: { group: UserGroup; role: OrgRole | null; tab: ListTab; onTab: (t: ListTab) => void }) {
  const data = useList((s) => s.data);
  const list = useRef<HTMLDivElement>(null);
  useListKeyboard(list, { open: (id) => navigate(href(id)) });
  const acc = useMemo(() => (data ? accuracySummary(data.accuracy.tests) : null), [data]);
  if (!data || !acc) return null;
  const meetings = new Set(data.filed.items.map((f) => f.introducedAt.date)).size;

  return (
    <Ctx.Provider value={{ group, data }}>
    <div className="page lg-list">
      <header className="page-header">
        <div className="page-header__title">
          <div className="title-row lg-title-row"><h1 className="page-title">Legislation</h1><ConnectionStatus /></div>
        </div>
        <div className="actions">
          <a className="btn" href="/dashboard/forecast-method"><Gauge size={14} />How forecasts work</a>
          {can(group, role, "csvExport") && <a className="btn" href={`/api/dashboard/legislation.csv?tab=${tab}`} download><Download size={14} />Export CSV</a>}
        </div>
      </header>

      <TabBar label="Legislation" active={tab} onChange={onTab} prefetch={() => listStore.getState().prefetch()} tabs={[
        { id: "vote", label: `Up for a vote · ${data.agenda.date}` },
        { id: "following", label: "Following", count: data.following.upForVote.length + data.following.decided.length },
        { id: "rollcalls", label: "Roll calls", count: `${data.rollCalls.total} divided` },
        { id: "filed", label: "Filed", count: `${meetings} meeting${meetings === 1 ? "" : "s"}` },
        { id: "accuracy", label: "Forecast accuracy", count: `${acc.called} of ${acc.total}` },
      ] satisfies { id: ListTab; label: string; count?: string | number }[]} />

      <div className={tab === "accuracy" ? "cols cols--full" : "cols"}>
        <div className="cols__main tab-panel" key={tab} ref={list} role="tabpanel">
          {tab === "vote" && <UpForVote data={data} onTab={onTab} />}
          {tab === "following" && <Following data={data.following} />}
          {tab === "rollcalls" && <RollCalls data={data.rollCalls} />}
          {tab === "filed" && <Filed items={data.filed.items} />}
          {tab === "accuracy" && <Accuracy data={data.accuracy} />}
        </div>
        {tab !== "accuracy" && (
          <aside className="cols__side">
            {tab === "filed" ? <FilingAlerts topics={data.alertTopics} next={data.filed.nextIntroductions} /> : <ListSide data={data} group={group} />}
          </aside>
        )}
      </div>
    </div>
    </Ctx.Provider>
  );
}

export { LIST_TABS };

/* ---------------- Signals per user group (25, 42, 43) ---------------- */
const Ctx = createContext<{ group: UserGroup; data: ListData | null }>({ group: "advocacy", data: null });
const lastName = (name: string) => name.replace(/,? (Jr\.|Sr\.|III|II)$/, "").split(" ").pop() ?? name;

/** The signal column depends on the user group (screenshots/25, 42, 43); from the starter's signalFor(). */
function signalFor(it: LegislationSummary, group: UserGroup): Signal {
  if (group === "resident" && it.myAlderperson) {
    const a = it.myAlderperson, label = `Ald. ${lastName(a.name)}`;
    if (it.rollCall && a.vote) return { kind: "vote", label: "Your alderperson voted", vote: a.vote };
    if (a.forecast) return { kind: "forecast", label, forecastLabel: a.forecast.label, perspective: "yes", p: a.forecast.p };
    if (!it.rollCall) return { kind: "backtest", label, text: "Not enough history" };
  }
  if (group === "office" && it.wardVoice && !it.rollCall) return { kind: "wardVoice", ...it.wardVoice };
  if (it.rollCall) return backtestSignal(it);
  return forecastSignal(it);
}

function useSignals() {
  const { group } = useContext(Ctx);
  return { upcoming: (it: LegislationSummary) => signalFor(it, group), decided: (it: LegislationSummary) => signalFor(it, group) };
}

/* ---------------- Up for a vote (25) ---------------- */

function UpForVote({ data, onTab }: { data: ListData; onTab: (t: ListTab) => void }) {
  const sig = useSignals();
  const mine = data.agenda.byCommittee.reduce((n, g) => n + g.items.filter((i) => i.mine).length, 0);
  const [scope, setScope] = useState<"mine" | "all">(mine ? "mine" : "all");
  const [open, setOpen] = useState<Record<string, boolean>>({});
  const groups = data.agenda.byCommittee
    .map((g) => ({ ...g, shown: scope === "all" || open[g.committee] ? g.items : g.items.filter((i) => i.mine) }))
    .filter((g) => g.shown.length > 0);
  return (<>
    <div className="lg-agenda-head">
      <SectionHeader title={`${data.agenda.meeting} · ${data.agenda.at}`} count={`${data.agenda.total} items`}>
        <span className="spacer" />
        <ViewSwitch label="Scope" value={scope} onChange={setScope} options={[{ id: "mine", label: `Your topics · ${mine}` }, { id: "all", label: `All ${data.agenda.total}` }]} />
      </SectionHeader>
    </div>
    {groups.map((g) => (
      <section key={g.committee} className="group">
        <GroupLabel count={g.total}>{g.committee}</GroupLabel>
        <List variant="plain" className="leg-list">{g.shown.map((it) => <LegislationRow key={it.recordId} item={it} signal={sig.upcoming(it)} />)}</List>
        {g.items.length > g.shown.length && (
          <div className="lg-more"><LinkArrow onClick={() => setOpen((o) => ({ ...o, [g.committee]: true }))}>Show {g.total - g.shown.length} more</LinkArrow></div>
        )}
      </section>
    ))}
    {data.recentFollowed.length > 0 && (
      <section className="group">
        <div className="lg-group-head">
          <SectionHeader title="Recent roll calls you follow"><span className="count-badge">Divided only</span></SectionHeader>
          <LinkArrow onClick={() => onTab("rollcalls")}>All {data.rollCalls.total} divided votes</LinkArrow>
        </div>
        <List variant="plain" className="leg-list">{data.recentFollowed.map((it) => <LegislationRow key={it.recordId} item={it} signal={sig.decided(it)} />)}</List>
      </section>
    )}
  </>);
}

/* ---------------- Following (31) ---------------- */

const CHANGE_TONE = { deferred: "warning", committee: "neutral", rollcall: "positive" } as const;
const CHANGE_LABEL = { deferred: "Deferred", committee: "Committee", forecast: "Forecast", rollcall: "Roll call" } as const;

function Following({ data }: { data: FollowingTab }) {
  const sig = useSignals();
  const items = data.follows.filter((f) => f.kind === "legislation");
  return (<>
    {data.changes.length > 0 && (
      <section className="catchup" aria-label="Since your last visit">
        <header><strong>Since your last visit · {data.lastVisit}</strong>
          {/* Saved per user (POST /api/dashboard/legislation/seen): stays seen across tabs, pages and visits. */}
          <button className="lg-catchup__seen" onClick={() => void listStore.getState().markSeen()}>Mark all seen<Check size={14} aria-hidden /></button></header>
        {data.changes.map((c) => (
          <div key={c.recordId + c.kind} className="catchup__line">
            <span className="lg-dot" aria-hidden />
            <span className="lg-catchup__chip">{c.kind === "forecast"
              ? <ForecastChip label="likely" perspective="yes" text="Forecast" className="fchip--plain" />
              : <span className={`chip chip--${CHANGE_TONE[c.kind]} lg-chip-11`}>{CHANGE_LABEL[c.kind]}</span>}</span>
            <span className="lg-catchup__text">{c.text}</span>
          </div>
        ))}
      </section>
    )}
    <div className="follow-chips">
      <span className="lg-follows__label">You follow</span>
      {items.length > 0 && <span className="follow-pill"><TypeBadge type="legislation" />{items.length} items</span>}
      {data.follows.filter((f) => f.kind !== "legislation").map((f) => (
        <span key={f.label} className="follow-pill"><TypeBadge type={f.kind} />{f.label}</span>
      ))}
      <button className="follow-pill follow-pill--add" onClick={() => openAddFollow()}><Plus size={12} aria-hidden />Follow</button>
    </div>
    {data.upForVote.length > 0 && (
      <section className="group">
        <GroupLabel count={data.upForVote.length}>Up for a vote{data.upForVote[0]?.agenda ? ` · ${data.upForVote[0].agenda.date}` : ""}</GroupLabel>
        <List variant="plain" className="leg-list">{data.upForVote.map((it) => <LegislationRow key={it.recordId} item={it} signal={sig.upcoming(it)} />)}</List>
      </section>
    )}
    {data.decided.length > 0 && (
      <section className="group">
        <GroupLabel count={data.decided.length}>Decided</GroupLabel>
        <List variant="plain" className="leg-list">{data.decided.map((it) => <LegislationRow key={it.recordId} item={it} signal={sig.decided(it)} />)}</List>
      </section>
    )}
  </>);
}

/* ---------------- Roll calls (32) ---------------- */

const PAGE = 8;

function RollCalls({ data }: { data: ListData["rollCalls"] }) {
  const [dividedOnly, setDividedOnly] = useState(true);
  const [category, setCategory] = useState("all");
  const [year, setYear] = useState(String(data.year));
  const [sort, setSort] = useState<"newest" | "oldest" | "closest">("newest");
  const [shown, setShown] = useState(PAGE);
  const cats = useMemo(() => [...new Set(data.items.map((i) => i.category.split(" · ").pop()!))].sort(), [data.items]);
  const years = useMemo(() => [...new Set(data.items.map((i) => i.lastAction.slice(0, 4)))].sort().reverse(), [data.items]);
  const items = data.items
    .filter((i) => i.rollCall && (!dividedOnly || (i.rollCall.nay > 0 && i.rollCall.yea > 0)))
    .filter((i) => category === "all" || i.category.endsWith(category))
    .filter((i) => i.lastAction.startsWith(year))
    .sort((a, b) => sort === "closest" ? Math.abs(a.rollCall!.yea - a.rollCall!.nay) - Math.abs(b.rollCall!.yea - b.rollCall!.nay)
      : sort === "oldest" ? a.lastAction.localeCompare(b.lastAction) : b.lastAction.localeCompare(a.lastAction));
  const closest = [...items].sort((a, b) => Math.abs(a.rollCall!.yea - a.rollCall!.nay) - Math.abs(b.rollCall!.yea - b.rollCall!.nay))[0];
  const more = (dividedOnly && category === "all" && year === String(data.year) ? data.total : items.length) - Math.min(shown, items.length);
  return (<>
    <div className="filters">
      <SelectButton label="Category" icon={<Shapes size={14} aria-hidden />} value={category} onChange={setCategory}
        options={[{ id: "all", label: `All ${data.categories} categories` }, ...cats.map((c) => ({ id: c, label: c }))]} />
      <SelectButton label="Year" icon={<CalendarDays size={14} aria-hidden />} value={year} onChange={setYear} options={years.map((y) => ({ id: y, label: y }))} />
      <SelectButton label="Sort" icon={<ArrowDownUp size={14} aria-hidden />} value={sort} onChange={setSort}
        options={[{ id: "newest", label: "Newest first" }, { id: "oldest", label: "Oldest first" }, { id: "closest", label: "Closest first" }]} />
      <label className="lg-divided"><button className="switch lg-switch-lg" role="switch" aria-checked={dividedOnly} aria-label="Divided only" onClick={() => setDividedOnly((v) => !v)} />Divided only</label>
    </div>
    <div className="stat-row">
      <div className="stat"><b>{data.total}</b><span>divided roll calls since {data.since}</span></div>
      <div className="stat"><b>{data.termKeyRollCalls.toLocaleString("en-US")}</b><span>key roll calls this term</span></div>
      {closest && <div className="stat"><b>{closest.rollCall!.yea}–{closest.rollCall!.nay}</b><span>closest this year ({closest.rollCall!.date.replace(/, \d{4}$/, "")})</span></div>}
    </div>
    <div>
      <List variant="plain" className="leg-list">
        {items.slice(0, shown).map((it) => (
          <LegislationRow key={it.recordId} item={it} sub="City Council roll call · 50 members" neutralOutcome
            signal={{ kind: "margin", yea: it.rollCall!.yea, nay: it.rollCall!.nay }} />
        ))}
      </List>
      <p className="lg-hint">
        {more > 0 && shown < items.length && <LinkArrow onClick={() => setShown((n) => n + 20)}>Show {more} more</LinkArrow>}
      </p>
    </div>
  </>);
}

/* ---------------- Filed (33) ---------------- */

function Filed({ items }: { items: FiledItem[] }) {
  const matches = items.filter((i) => i.matchedTopic).length;
  const [scope, setScope] = useState<"mine" | "all">(matches ? "mine" : "all");
  const [open, setOpen] = useState<Record<string, boolean>>({});
  const byMeeting = useMemo(() => {
    const m = new Map<string, FiledItem[]>();
    for (const it of items) {
      if (scope === "mine" && !it.matchedTopic) continue;
      const k = `${it.introducedAt.meeting} · ${it.introducedAt.date}`;
      m.set(k, [...(m.get(k) ?? []), it]);
    }
    return [...m.entries()];
  }, [items, scope]);
  const meetings = new Set(items.map((f) => f.introducedAt.date)).size;
  return (<>
    <SectionHeader title="Newly filed" count={`${meetings} meeting${meetings === 1 ? "" : "s"}`}>
      <span className="spacer" />
      <ViewSwitch label="Scope" value={scope} onChange={setScope} options={[{ id: "mine", label: `Matches your topics · ${matches}` }, { id: "all", label: "Everything" }]} />
    </SectionHeader>
    {byMeeting.map(([meeting, list]) => (
      <section key={meeting} className="group">
        <GroupLabel count={`${list.filter((i) => i.matchedTopic).length} match`}>Introduced at {meeting}</GroupLabel>
        <List as="div" variant="plain" className="leg-list filed-list">
        {groupFiled(list).map((g) => g.kind === "item" ? <FiledRow key={g.item.recordId} it={g.item} /> : (
          <div key={g.key} className="lg-filed-group">
            <ListRow as="div" className="filed-row" data-row={g.key} tabIndex={0}>
              <RowMain min={160} className="leg-row__main">
                <span className="leg-row__meta">
                  <TypeBadge type="legislation" />
                  <span>{g.items.length} similar items · {g.items.slice(0, 6).map((x, i) => (i ? x.recordId.replace(/^[A-Z]+\d{4}/, "") : x.recordId)).join(", ")}{g.items.length > 6 ? ` and ${g.items.length - 6} more` : ""}</span>
                </span>
                <RowTitle className="leg-row__title">{g.items[0].groupTitle ?? g.items[0].title}</RowTitle>
                <RowSub wrap className="leg-row__sub">{g.items[0].sponsor} · referred to {g.items[0].committee}{g.items[0].groupDetail ? ` · ${g.items[0].groupDetail}` : ""}</RowSub>
              </RowMain>
              <TopicMatch topic={g.items[0].matchedTopic} />
              <RowCell width={110} align="end" className="lg-filed__action">
                <button className="btn btn--small lg-btn-sm" aria-expanded={!!open[g.key]} onClick={() => setOpen((o) => ({ ...o, [g.key]: !o[g.key] }))}>
                  <ChevronsUpDown size={14} aria-hidden />{open[g.key] ? "Hide" : `Show ${g.items.length}`}
                </button>
              </RowCell>
            </ListRow>
            {open[g.key] && <div className="filed-group__items">{g.items.map((it) => <FiledRow key={it.recordId} it={it} />)}</div>}
          </div>
        ))}
        </List>
      </section>
    ))}
  </>);
}

const TopicMatch = ({ topic }: { topic?: string }) => (
  <RowCell width={170} className="topic-match">{topic && (<><span className="eyebrow">Matches your topic</span><span className="topic-chip"><Tag size={12} aria-hidden />{topic}</span></>)}</RowCell>
);

function FiledRow({ it }: { it: FiledItem }) {
  const followed = useFollows((s) => findFollow(s.follows, "legislation", it.recordId));
  const following = !!followed;
  return (
    <ListRow as="div" className="filed-row" data-row={it.recordId} tabIndex={0}>
      <RowMain min={160} className="leg-row__main">
        <span className="leg-row__meta"><TypeBadge type="legislation" /><a className="leg-row__record" href={href(it.recordId)}>{it.recordId}</a></span>
        <RowTitle className="leg-row__title">{it.title}</RowTitle>
        <RowSub wrap className="leg-row__sub">{it.sponsor} · referred to {it.committee}</RowSub>
      </RowMain>
      <TopicMatch topic={it.matchedTopic} />
      <RowCell width={110} align="end" className="lg-filed__action">
        <button className="btn btn--small lg-btn-sm" aria-pressed={following} onClick={() => {
          if (followed) void followsStore.getState().unfollow(followed.id);
          else void followsStore.getState().follow({ type: "legislation", refId: it.recordId, label: it.title });
        }}>{following ? <Check size={14} aria-hidden /> : <Plus size={14} aria-hidden />}{following ? "Following" : "Follow"}</button>
      </RowCell>
    </ListRow>
  );
}

/* ---------------- Forecast accuracy (34) ---------------- */

function Accuracy({ data }: { data: ListData["accuracy"] }) {
  const s = accuracySummary(data.tests);
  const b = data.calibration.length ? data.calibration.reduce((a, x) => (Math.abs(x.expected - 0.7) < Math.abs(a.expected - 0.7) ? x : a)) : null;
  const missed = data.tests.find((t) => t.passed !== t.forecastPassed && t.note);
  const short = (d: string) => d.replace(/, \d{4}$/, "");
  return (<>
    <div className="stat-row stat-row--4">
      <div className="stat"><b>{s.called} of {s.total}</b><strong>outcomes called right</strong><span>Last {s.total} divided roll calls</span></div>
      <div className="stat"><b>±{s.medianMiss} votes</b><strong>median miss on the tally</strong><span>Worst miss: {s.worstMiss}{s.worstAt ? ` (${short(s.worstAt)})` : ""}</span></div>
      <div className="stat"><b>{Math.round((data.memberCalled.right / Math.max(1, data.memberCalled.of)) * 100)}%</b><strong>member votes called right</strong><span>{data.memberCalled.right.toLocaleString("en-US")} of {data.memberCalled.of.toLocaleString("en-US")} member-votes</span></div>
      {b && <div className="stat"><b>{Math.round(b.actual * 100)}%</b><strong>when we said {Math.round(b.expected * 10) * 10}%</strong>
        <span>Calibrated within {Math.max(5, Math.ceil(Math.max(...data.calibration.map((c) => Math.abs(c.actual - c.expected))) * 20) * 5)} points</span></div>}
    </div>
    <div className="accuracy">
      <section className="calibration" aria-label="Calibration">
        <h2 className="lg-h2">Calibration</h2>
        <div className="calibration__rows">
          {data.calibration.map((c) => (
            <div key={c.label} className="calibration__row">
              <span>{c.label}</span>
              <span className="calibration__track" role="img" aria-label={`Forecast ${Math.round(c.expected * 100)}%, happened ${Math.round(c.actual * 100)}%`}>
                <span className="calibration__bar" style={{ width: `${c.actual * 100}%` }} />
                <span className="calibration__tick" style={{ left: `${c.expected * 100}%` }} />
              </span>
              <span className="calibration__v">{Math.round(c.actual * 100)}% · n={c.n}</span>
            </div>
          ))}
        </div>
        <p className="lg-note">Bar = what happened · black tick = what we forecast</p>
      </section>
      <section className="backtest">
        <SectionHeader title="Every divided vote, forecast vs. result" count={`Last ${s.total}`} />
        <p className="lg-muted-14">{data.tests.some((t) => t.basis === "retrospective") ? "From inputs dated before each vote" : "Frozen the day before each vote"}</p>
        {data.model && <p className="lg-muted-14">{modelLine(data.model, data.frozen)}</p>}
        <table>
          <thead><tr><th>Vote</th><th>Result</th><th>Forecast</th><th>Miss</th><th>Called</th></tr></thead>
          <tbody>
            {data.tests.map((t) => {
              const ok = t.passed === t.forecastPassed;
              return (
                <tr key={t.recordId} data-missed={!ok || undefined} data-row={t.recordId} tabIndex={0}>
                  <td><b>{t.title}</b><span>{t.recordId} · {short(t.date)}</span></td>
                  <td className="lg-num-strong">{t.yea}–{t.nay}</td><td>{t.forecastYea}–{t.forecastNay}</td><td>{Math.abs(t.yea - t.forecastYea)}</td>
                  <td>{ok ? <span className="chip chip--positive lg-chip-11">Yes</span> : <span className="lg-missed">Missed</span>}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {missed?.note && <p className="lg-backtest__note">{missed.note}</p>}
      </section>
    </div>
  </>);
}

/** One line on the learned model (copy pending designer review): "Model v2026-10-10.1 · Brier 0.11 · trained on 4,120 member-votes (310 frozen) · frozen forecasts: 290 of 310 called". */
export function modelLine(m: NonNullable<ListData["accuracy"]["model"]>, frozen?: ListData["accuracy"]["frozen"]) {
  const n = (x: number) => x.toLocaleString("en-US");
  const parts = [m.version === "prior" ? "Model: the hand-tuned prior (no trained model has beaten it yet)" : `Model ${m.version}`];
  if (m.holdout) parts.push(`Brier ${m.holdout.brier.toFixed(2)}`, `log loss ${m.holdout.logloss.toFixed(2)}`);
  if (m.trainedOn) parts.push(`trained on ${n(m.trainedOn.backtest + m.trainedOn.frozen)} member-votes (${n(m.trainedOn.frozen)} frozen)`);
  if (frozen && frozen.memberCalled.of > 0) parts.push(`frozen forecasts: ${n(frozen.memberCalled.right)} of ${n(frozen.memberCalled.of)} member votes called`);
  return parts.join(" · ");
}

/* ---------------- Side cards (25, 31, 32) ---------------- */

function ListSide({ data, group }: { data: ListData; group: UserGroup }) {
  const items = [...data.following.upForVote, ...data.agenda.byCommittee.flatMap((g) => g.items)];
  const next = items.find((i) => i.forecast && i.mine) ?? items.find((i) => i.forecast);
  const s = accuracySummary(data.accuracy.tests);
  const council = next?.forecast && <CouncilCard item={next} kicker={`${group === "office" ? "Whip count" : "Next big vote"} · ${next.agenda?.date ?? ""}`}
    title={next.title.split(":")[0]} link={`${href(next.recordId)}?tab=forecast`} />;
  const accuracy = (
    <section className="card">
      <h3 className="lg-card__title">Forecast accuracy</h3>
      <div className="lg-mini-stats">
        <div><b>{s.called} of {s.total}</b><span>outcomes called right</span></div>
        <div><b>±{s.medianMiss}</b><span>median miss, in votes</span></div>
      </div>
      <p className="lg-card__sub">Backtested on the last {s.total} divided roll calls, using only data available the day before each vote.</p>
    </section>
  );
  if (group === "resident") return (<>
    {data.resident ? <YourAlderperson r={data.resident} next={next} /> : <NoWard />}
    {data.resident && <HowWouldYouVote r={data.resident} />}
    {council}
    {accuracy}
  </>);
  if (group === "office") return (<>
    {data.office && <YourWardSaid w={data.office} />}
    {council}
    {accuracy}
  </>);
  return (<>
    {council}
    {data.tensionByTopic.length > 0 && (
      <section className="card">
        <h3 className="lg-card__title">Council tension by topic</h3>
        <p className="lg-card__sub">Split roll calls, last 12 months</p>
        {data.tensionByTopic.map((x) => <div key={x.topic} className="row-between"><span className="lg-13">{x.topic}</span><TensionGauge t={x.t} /></div>)}
      </section>
    )}
    {accuracy}
  </>);
}

const ordinal = (n: number) => `${n}${n % 100 >= 11 && n % 100 <= 13 ? "th" : ["th", "st", "nd", "rd"][n % 10] ?? "th"}`;

/** Residents (42): their alderperson, how they're likely to vote on your items, call + script. */
function YourAlderperson({ r, next }: { r: NonNullable<ListData["resident"]>; next?: LegislationSummary }) {
  const a = r.alderperson;
  const [script, setScript] = useState(false);
  return (
    <section className="card lg-alder-card">
      <div className="lg-alder__who"><Face name={a.name} photoUrl={a.photoUrl} size={44} />
        <span><span className="eyebrow lg-kicker">Your alderperson · Ward {a.ward}</span><b className="lg-alder__name">{a.name}</b></span></div>
      {r.summary && <p className="lg-team__body">{r.summary}</p>}
      {script && next && (
        <div className="lg-script"><span className="eyebrow">Call script</span>
          <p>"Hi, I live in the {ordinal(a.ward)} Ward. I'm calling about the {(next.shortTitle || next.title).toLowerCase().replace(/&/g, "and")}, {next.recordId}{next.agenda ? `, up for a vote ${next.agenda.dateLong ?? next.agenda.date}` : ""}. I {next.myPosition ?? "support"} it because..."</p>
        </div>
      )}
      <div className="lg-alder__contact">
        {a.phone && <a className="btn btn--primary" href={`tel:${a.phone.replace(/[^\d+]/g, "")}`}><Phone size={16} aria-hidden />{a.phone}</a>}
        <button className="btn" aria-expanded={script} onClick={() => setScript((v) => !v)}><FileText size={16} aria-hidden />Call script</button>
      </div>
    </section>
  );
}

/** Residents (42): one position per item; only ward totals reach the office. Clicking the chosen side again clears it. */
function HowWouldYouVote({ r }: { r: NonNullable<ListData["resident"]> }) {
  const [stances, setStances] = useState(r.stances);
  if (!stances.length) return null;
  const choose = (id: string, v: "support" | "oppose") => {
    const cur = stances.find((x) => x.recordId === id)?.stance;
    const next = cur === v ? null : v;
    setStances((xs) => xs.map((x) => (x.recordId === id ? { ...x, stance: next } : x)));
    void fetch(`/api/dashboard/legislation/${encodeURIComponent(id)}/stance`, { method: next ? "PUT" : "DELETE", credentials: "include",
      headers: { "content-type": "application/json" }, body: JSON.stringify({ position: next }) }).catch(() => {});
  };
  return (
    <section className="card lg-hwyv">
      <h3 className="lg-card__title">How would you vote?</h3>
      <p className="lg-card__sub">Your ward's totals go to your alderperson's office. Never your name.</p>
      <div className="lg-hwyv__rows">{stances.map((x) => (
        <div key={x.recordId} className="lg-hwyv__row">
          <span>{x.title}</span>
          <span className="seg lg-hwyv__seg" role="radiogroup" aria-label={`How would you vote on ${x.title}`}>
            {(["support", "oppose"] as const).map((v) => (
              <button key={v} role="radio" aria-checked={x.stance === v} data-v={v} onClick={() => choose(x.recordId, v)}>{v === "support" ? "Support" : "Oppose"}</button>
            ))}
          </span>
        </div>
      ))}</div>
      <p className="lg-card__sub">You've weighed in on {stances.filter((x) => x.stance).length} of {stances.length}</p>
    </section>
  );
}

/** Offices (43): "How would you vote?" totals from verified residents of the ward. */
function YourWardSaid({ w }: { w: NonNullable<ListData["office"]> }) {
  const rows = w.items.filter((x) => wardSaid(x).enough).slice(0, 3);
  if (!rows.length) return null;
  return (
    <section className="card lg-wardsaid">
      <h3 className="lg-card__title">Your ward said</h3>
      <p className="lg-card__sub lg-card__sub--lh">Verified Ward {w.ward} residents · totals only</p>
      {rows.map((x) => { const v = wardSaid(x); return (
        <div key={x.recordId} className="lg-wardsaid__row">
          <div className="row-between"><span>{x.title}</span><b data-tone={v.tone}>{v.share}% · {v.n}</b></div>
          <div className="ward-voice"><span className="is-support" style={{ flexGrow: x.support }} /><span className="is-oppose" style={{ flexGrow: x.oppose }} /></div>
        </div>
      ); })}
      <LinkArrow href="/api/dashboard/legislation.csv?tab=ward" icon={<Download size={14} aria-hidden />}>Export for your newsletter</LinkArrow>
    </section>
  );
}

/** "NEXT BIG VOTE · OCT 14" / "COUNCIL FORECAST · OCT 14" card (25, 26). */
export function CouncilCard({ item, kicker, title, link, onToss }: { item: LegislationSummary; kicker: string; title?: string; link?: string; onToss?: () => void }) {
  const f = item.forecast!;
  const label = councilLabel(f);
  return (
    <section className="card">
      <header className="row-between"><Kicker>{kicker}</Kicker>
        <ForecastChip label={label} perspective="yes" text={label === "likely" ? "Likely passes" : label === "unlikely" ? "Likely fails" : "Toss-up"} /></header>
      {title && <h3 className="lg-card__big">{title}</h3>}
      <ProjectionBar likelyYes={f.likelyYes} tossup={f.tossup} likelyNo={f.likelyNo} />
      <p className="lg-card__body">Needs {f.needed} yes votes. Projected {f.projectedYes}–{f.projectedNo}, with a range of {f.range[0]} to {f.range[1]}.</p>
      <div className="row-between"><span className="lg-11-muted">Council tension on {f.topic}</span><TensionGauge t={f.tension} strong /></div>
      {link
        ? <a className="btn lg-btn-block" href={link} onClick={(e) => { if (link.startsWith(location.pathname)) { e.preventDefault(); navigate(link); } }}><Users size={14} aria-hidden />See the {f.tossup} toss-ups</a>
        : <button className="btn lg-btn-block" onClick={onToss}><Users size={14} aria-hidden />See the {f.tossup} toss-ups</button>}
    </section>
  );
}

function FilingAlerts({ topics, next }: { topics: ListData["alertTopics"]; next?: string }) {
  const [state, setState] = useState(topics);
  const save = (topic: string, on: boolean) => {
    setState((s) => s.map((x) => (x.topic === topic ? { ...x, on } : x)));
    void fetch("/api/dashboard/settings", { method: "PUT", credentials: "include", headers: { "content-type": "application/json" },
      body: JSON.stringify({ filingAlerts: { [topic]: on } }) }).catch(() => {});
  };
  return (<>
    <section className="card">
      <h3 className="lg-card__title">Alerts for new filings</h3>
      <div className="lg-alerts">
        {state.map((t) => (
          <div key={t.topic} className="row-between">
            <span className="lg-alert-topic"><TypeBadge type="topic" />{t.topic}</span>
            <button className="switch lg-switch-lg" role="switch" aria-checked={t.on} aria-label={`Alerts for ${t.topic}`} onClick={() => save(t.topic, !t.on)} />
          </div>
        ))}
      </div>
      <LinkArrow onClick={() => openAddFollow("topic")} icon={<Plus size={14} aria-hidden />}>Add a topic or keyword</LinkArrow>
    </section>
    {next && (
      <section className="lg-note-box">
        <strong>Next introductions</strong>
        <p>New items are introduced at the City Council meeting on {next}. Matches show up here within an hour.</p>
      </section>
    )}
  </>);
}

export { CountBadge };
